import React, { useContext, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionCollapse, AccordionContext, Form, Modal, useAccordionToggle } from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../contexts/basket/useBasketContext';
import { Item, Timeslot } from '../../../../../contexts/site/site-context';
import useSiteContext from '../../../../../contexts/site/useSiteContext';
import { money } from '../../../../../services/format';
import { offerHeadline, offerNames } from '../../../../../services/offers';
import useOfferEstimate from '../../../../../hooks/useOfferEstimate';
import CheckoutButton from '../basket/CheckoutButton';
import OrderItem from '../order/OrderItem';
import TimeslotSelectTwoPart from '../../../../timeslots/TimeslotSelectTwoPart';
import useOperatorContext from '../../../../../contexts/operator/useOperatorContext';
import {useLocalStorage} from "../../../../../hooks/localstorage";
import MenuItem from "../items/MenuItem";
import {Preferences} from "../../MenuPage";
import {trackEvent} from "../../../../../services/api/operator";
import {getDeliveryFee} from "../../../../../services/api/basket";
import PageSpinnerThree from "../../../../general/PageSpinnerThree";
import RadioGroup from "../../../../general/RadioGroup";

type OrderModalProps = {
    onHide: () => void;
    show: boolean;
    reset: boolean;
    items: Item[];
    showItemModal: (item: Item, edit: boolean) => void;
    displayUpsell: boolean;
    onContinueToUpsell: () => void;
    injectShowTimeslotHelper: boolean;
    injectTimeslotError: string | null;
    injectUnavailableScheduledItems: string[];
    preferences: Preferences
};

const OrderModal = ({ show, onHide, reset = false, items, showItemModal, displayUpsell, onContinueToUpsell, injectShowTimeslotHelper, injectTimeslotError, injectUnavailableScheduledItems, preferences }: OrderModalProps) => {
    const basketContext = useBasketContext();
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();

    const [navHorizontalScroll, setNavHorizontalScroll] = useState(0);
    const [navHorizontalScrollMax, setNavHorizontalScrollMax] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const bodyContent = useRef<HTMLDivElement>(null);
    const [deliveryFee, setDeliveryFee] = useState(0);
    // what the basket gets off from an offer, as far as can be told before it is validated
    const offerEstimate = useOfferEstimate(deliveryFee);
    const offerDiscount = offerEstimate?.discount ?? 0;
    const [fetchingTimeslots, setFetchingTimeslots] = useState(false);
    const [fetchingDeliveryFee, setFetchingDeliveryFee] = useState(false);

    const [shadowTop, setShadowTop] = useState(false);
    const [shadowBottom, setShadowBottom] = useState(false);
    const [showTimeslotHelper, setShowTimeslotHelper] = useState(false);
    const [unavailableScheduledItems, setUnavailableScheduledItems] = useState<string[]>([]);
    const [timeslotError, setTimeslotError] = useState<string|null>(null);
    const [storedBasket, setStoredBasket] = useLocalStorage('basket', null);

    const CUSTOMER_NOTES_LIMIT = 100;
    const [customerNotes, setCustomerNotes] = useState("");

    const address = JSON.parse(localStorage.getItem('address') ?? '{}');

    useEffect(() => {
        if (reset) {
            basketContext.clearBasket();
        }
    }, []);

    useEffect(() => {
        setShowTimeslotHelper(injectShowTimeslotHelper);
        setTimeslotError(injectTimeslotError);
        setUnavailableScheduledItems(injectUnavailableScheduledItems);
    }, [injectShowTimeslotHelper, injectTimeslotError, injectUnavailableScheduledItems]);

    useEffect(() => {
        if (items.length === 0) {
            onHide();
        }
    }, [items]);

    useEffect(() => {
        function updateScrollPosition() {
            if (bodyContent && bodyContent.current ) {

                if(bodyContent?.current?.scrollTop > 78) {
                    setShadowTop(true)
                } else {
                    setShadowTop(false)
                }
                if (bodyContent?.current?.scrollHeight - bodyContent?.current?.scrollTop === bodyContent?.current?.clientHeight) {
                    setShadowBottom(false)
                } else {
                    setShadowBottom(true)
                }
            }
        }

        updateScrollPosition();

        if (bodyContent && bodyContent.current) {
            bodyContent.current.addEventListener("scroll", updateScrollPosition, false);

            document.getElementById('order-modal')?.classList.add('anim-enter');

            return () => {
                setShadowTop(false);
                setShadowBottom(false);
                bodyContent?.current?.removeEventListener("scroll", updateScrollPosition, false);
                document.getElementById('order-modal')?.classList.remove('anim-enter');
            };
        }
    }, [bodyContent.current, show]);

    useEffect(() => {
        if ( show ) {
            setFetchingTimeslots(true);

            siteContext.fetchTimeslots(
                siteContext.site?.uid ?? basketContext.validatedSite?.uid ?? '',
                siteContext.flow ?? 'noflow');

            setFetchingTimeslots(false);

            trackEvent('open-order', siteContext.site?.uid ?? '');
        }

        if ( show && ['delivery', 'catering', 'venues'].includes(siteContext.channel) ) {
            updateDeliveryFee();
        }
    }, [ show ]);

    useEffect(() => {
        if ( siteContext.channel === 'dine_in' && siteContext.timeslots ) {
            basketContext.setTimeslot(siteContext.timeslots[0]);
        }
    }, [ siteContext.timeslots ]);

    useEffect(() => {
        if ( ['delivery', 'catering', 'venues'].includes(siteContext.channel) && siteContext.timeslots ) {

            // if the user has selected a slot
            // and the slot chosen is the first one of the list
            // we don't run the update because the price already
            // reflects the first slot..
            if ( basketContext.timeslot && basketContext.timeslot.id === siteContext.timeslots[0]['id'] ) {
                return;
            }

            updateDeliveryFee(basketContext.timeslot?.id);
        }

    }, [ basketContext.timeslot?.id, basketContext.basketItems ])

    const updateDeliveryFee = async (slot: string = '') => {
        if ( !siteContext || !siteContext.site || !basketContext || !show) {
            return;
        }

        setFetchingDeliveryFee(true);
        setTimeslotError(null);

        try {
            await getDeliveryFee(
                items,
                siteContext.site.uid,
                localStorage.getItem('delivery-venue') ?? '',
                localStorage.getItem('address') ?? '',
                siteContext.flow ?? 'noflow',
                siteContext.channel,
                basketContext.timeslot?.id ?? '',
                basketContext.timeslot?.is_asap ?? false
            )
                .then( (data) => {
                    setDeliveryFee(data.delivery_fee);
                    setFetchingDeliveryFee(false);

                    if ( data.timeslots ) {
                        siteContext.setTimeslots(data.timeslots);
                    }

                });

        } catch (error: any) {
            if (error.code && [847, 848, 849, 862].includes(error.code)) {
                setTimeslotError(error.message);
                if ( error.extra_data.length > 0 ) {
                    siteContext.setTimeslots(error.extra_data);
                }
            }
            setFetchingDeliveryFee(false);
        }
    };

    function CustomToggle({ children, eventKey, callback }: any) {
        const currentEventKey = useContext(AccordionContext);

        const isCurrentEventKey = currentEventKey === eventKey;

        const decoratedOnClick = useAccordionToggle(eventKey, () => {
            callback && callback(eventKey);
        });

        return (
            <button
                type="button"
                className={`your-order-toggle customToggle ${isCurrentEventKey ? 'show' : ''}`}
                onClick={decoratedOnClick}
            >
                {children}
                <span className="material-symbols-outlined">keyboard_arrow_down</span>
            </button>
        );
    }

    return (
        <StyledModal
            show={show}
            onHide={onHide}
            size="lg"
            backdrop={true}
            backdropClassName="modal-order-backdrop"
            className="order-modal-dialog"
            centered
            id={'order-modal'}
            animation={false}
        >
            <Modal.Header className="modal-show-header-shadow">
                <h2 className='modal__title'>{t`Order summary`}</h2>
                <div className="modal-close__wrapper">
                    <button role="button" className={'modal-close__btn'} onClick={() => onHide()}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body ref={bodyContent}>
                {siteContext.channel === 'collection' && (
                    <>
                        <h3 className="channel channel-reduced">{t`Collection`} &middot; {siteContext.site?.name}</h3>
                    </>
                )}

                { ['delivery', 'catering'].includes(siteContext.channel) && (
                    <>
                        <h3 className="channel channel-reduced">{t`Delivery`} &middot; { localStorage.getItem('postcode') ?? '/' }</h3>
                    </>
                )}

                {siteContext.channel === 'venues' && (
                    <>
                        <h3 className="channel channel-reduced">{t`Delivery`} &middot; { address.deliveryVenueName ?? localStorage.getItem('postcode') ?? '/' }</h3>
                    </>
                )}

                {siteContext.channel === 'dine_in' && (
                    <>
                        <h3 className="channel channel-reduced">{t`Table`} {basketContext.tableNumber}</h3>
                    </>
                )}

                <OrderInfoSeparator>
                    <BasketItemsWrapper className="modal-block">
                        {items.map((item, index) => (
                            <OrderItem
                                item={item}
                                index={index}
                                key={`basket-item-${index}-${item.uid}`}
                                showItemModal={ () => { showItemModal(item, true) }}
                                unavailableScheduledItem={ unavailableScheduledItems.includes(item.uid) }
                            />
                        ))}
                    </BasketItemsWrapper>
                </OrderInfoSeparator>

                {siteContext.channel !== 'dine_in' && operatorContext.operator?.theme.v4.customer_notes && (
                    <OrderInfoSeparator>
                        <StyledAccordion className="modal-block" defaultActiveKey="-1">
                            <CustomToggle eventKey="0">
                                <div className="toggle">
                                    <span className="toggle__title">{t`Add a note to your order`}</span>
                                </div>
                            </CustomToggle>
                            <AccordionCollapse eventKey="0" timeout={2000}>
                                <>
                                    <OrderNote
                                        placeholder={t`Add a note for the restaurant (optional)`}
                                        value={customerNotes}
                                        maxLength={CUSTOMER_NOTES_LIMIT}
                                        className={'form-control--textarea'}
                                        onChange={(e) => { setCustomerNotes(e.target.value); basketContext.setSpecialNotes(e.target.value) } }
                                    />
                                    <div className="customer-notes-counter">{ customerNotes.length } / { CUSTOMER_NOTES_LIMIT } characters</div>
                                </>
                            </AccordionCollapse>
                        </StyledAccordion>
                    </OrderInfoSeparator>
                )}

                <OrderInfoSeparator>
                    <LineItem className="modal-block">
                        <div className="basket__subtotal">
                            <span className="d-block w-100">{t`Subtotal`}</span>
                            <span>
                                { money(basketContext.price.subtotal, operatorContext.operator?.ccy, operatorContext.operator?.locale) }
                            </span>
                        </div>
                        { offerEstimate && (
                            <div className="basket__subtotal offer-discount">
                                <span className="d-block w-100">
                                    { offerHeadline(offerEstimate.offer, offerNames(offerEstimate.offer, siteContext.menu), (pence: number) => money(pence, operatorContext.operator?.ccy, operatorContext.operator?.locale)) }
                                </span>
                                <span className="non_wrapped">
                                    { money(-offerDiscount, operatorContext.operator?.ccy, operatorContext.operator?.locale) }
                                </span>
                            </div>
                        )}
                        { ['delivery', 'catering', 'venues'].includes(siteContext.channel) && (
                            <>
                                <div className="basket__subtotal">
                                    <span className="d-block w-100">{t`Delivery fee`}</span>
                                    <span className={ fetchingDeliveryFee ? 'is-loading' : '' }>
                                        <PageSpinnerThree
                                            show={fetchingDeliveryFee}
                                            text={ deliveryFee === 0 ? t`Free` : money(deliveryFee, operatorContext.operator?.ccy, operatorContext.operator?.locale) }
                                        />
                                    </span>
                                </div>
                            </>
                        )}
                    </LineItem>
                </OrderInfoSeparator>

                <StyledForm>
                    <Form.Group controlId="orderForm" className="mb-0">
                        { siteContext.channel !== 'dine_in' && (
                            <StickyElement>
                                <h3 className="channel">{ ['delivery', 'catering', 'venues'].includes(siteContext.channel) ? 'Delivery' : 'Collection' } time</h3>
                                { showTimeslotHelper && !basketContext.timeslot && (
                                    <ErrorContainer>
                                        <span className="material-symbols-outlined">info</span>
                                        <span>Please select a { ['delivery', 'catering', 'venues'].includes(siteContext.channel) ? 'delivery' : 'collection' } time.</span>
                                    </ErrorContainer>
                                )}
                                { timeslotError && (
                                    <ErrorContainer>
                                        <span className="material-symbols-outlined">info</span>
                                        <span>{timeslotError}</span>
                                    </ErrorContainer>
                                )}
                                <TimeslotSelectTwoPart
                                    timeslots={siteContext.timeslots}
                                    selectedTimeslotId={basketContext.timeslot?.id}
                                    disabledSelect={fetchingTimeslots || fetchingDeliveryFee}
                                    onChange={() => { setUnavailableScheduledItems([]) }}
                                />
                            </StickyElement>
                        )}
                    </Form.Group>
                </StyledForm>
            </Modal.Body>
            <Modal.Footer className="modal-show-footer-shadow">
                <StyledForm>
                    <div className="total">
                        <strong>{t`Total`}</strong>
                        <span className={ fetchingDeliveryFee ? 'is-loading' : '' }>
                            <PageSpinnerThree
                                show={fetchingDeliveryFee}
                                text={ money(basketContext.price.subtotal + deliveryFee - offerDiscount, operatorContext.operator?.ccy, operatorContext.operator?.locale) }
                            />
                        </span>
                    </div>
                    <Form.Group controlId="checkoutForm" className="mb-0">
                        {items.length ? <CheckoutButton
                            showTimeslotHelper={setShowTimeslotHelper}
                            setTimeslotError={setTimeslotError}
                            setUnavailableScheduledItems={setUnavailableScheduledItems}
                            currentlyOnUpsell={false}
                            displayUpsell={displayUpsell}
                            onContinueToUpsell={onContinueToUpsell}
                        /> : null}
                    </Form.Group>
                </StyledForm>
            </Modal.Footer>
        </StyledModal>
    );
};

const ErrorContainer = styled.div`
    color: var(--invalid);
    margin-top: -16px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    & span { margin-right: 4px; }
`;

const StickyElement = styled.div`
    padding: 0 16px;
    margin-bottom: 16px;
`;

const OrderNote = styled.textarea`
    margin-top: 8px;
    border: 1px solid var(--border-grey);
    font-size: 16px;
    line-height: 24px;
    border-radius: var(--radius_m);
    padding: 17px;
    width: 100%;
    height: 96px;
    resize: none;

    &::placeholder {
        color: var(--text-grey);
        font-size: 16px;
    }
`;

const LineItem = styled.div`
    padding: 0 16px;

    .non_wrapped {
        white-space: nowrap;
    }

    .form-group,
    .form-label {
        margin: 0;
    }
    .basket__subtotal {
        display: flex;
        align-items: center;

        &:not(:first-child) {
            padding-top: 16px;
        }

        > span {
            position: relative;
            color: var(--text-black);
            font-size: 16px;
            line-height: 24px;
            font-weight: 500;

            &.is-loading {
                min-width: 65px;
                right: -10px;
            }
        }
    }

    .basket__tip {
        padding: 8px 0;
        display: block;
    }
`;

const BasketItemsWrapper = styled.div`
    padding: 0 8px 8px 8px !important;
`;

const StyledForm = styled(Form)`
    .form-group {
        margin-bottom: 16px;
    }

    width: 100%;
`;

const StyledAccordion = styled(Accordion)`
    padding: 0 16px;

    .customToggle {
        transition: background-color 0.15s ease-in-out;

        .material-symbols-outlined {
            transition: transform 0.15s ease-in-out;
            font-size: 24px;
            line-height: 24px;
        }

        &.show {
            .material-symbols-outlined {
                transform: rotate(180deg);
            }
        }
    }

    > button {
        width: 100%;
        background: transparent;
        border: none;
        outline: none;
        box-shadow: none;
        display: flex;
        padding: 0;
        margin: 0;
        font-weight: 600;
        align-items: center;
        text-align: left;
        color: var(--text_body_color);

        img {
            transition: transform 0.2s linear;
        }

        &.show {
            img {
                transform: rotate(180deg);
            }
        }

        .toggle {
            display: flex;
            flex-wrap: wrap;
            font-size: 16px;
            font-weight: 500;
            line-height: 24px;
            color: var(--text_body_color);

            > span {
                margin-right: 8px;
            }
        }
    }

    .customer-notes-counter {
        padding-top: 4px;
        text-align: right;
        font-size: 14px;
        line-height: 20px;
        color: var(--text-grey);
    }
`;

const OrderInfoSeparator = styled.div`
    margin-bottom: 16px;
    border-bottom: 1px solid var(--border-grey);
`;

const StyledModal = styled(Modal)`
    .modal-block {
        padding-bottom: 16px;
    }

    .close {
        left: 16px;
        right: auto;
        opacity: 1 !important;
    }

    .modal-content {
        @media screen and (max-width: 767px) {
            height: 100% !important;
        }
    }

    .modal-header {
        display: flex;
        align-items: center;
        height: 56px;
        background-color: transparent;
        z-index: 1;
        width: 100%;
        padding: 16px;

        > div { width: 100% }

        .modal__title {
            margin-bottom: 0;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
        }
    }

    .modal-body {
        padding: 0;
        padding-bottom: 10px;
        overflow-y: auto;
        font-size: 14px;

        .channel {
            margin-bottom: 16px;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
        }

        .channel-reduced {
            margin-bottom: 8px !important;
            padding: 16px 16px 0 16px;
        }

        table {
            table-layout: fixed;
            margin: 1.5rem 0;
            tr td {
                line-height: 30px;
                padding: 0;
                border: 0;
                font-weight: 600;
                &:last-child {
                    font-weight: 400;
                    color: var(--text-black);
                }
            }
        }
    }

    .modal-footer {
        padding: 16px !important;
        transition: box-shadow 0.15s ease-in-out;

        .total {
            padding-bottom: 8px;
            display: flex;
            justify-content: space-between;
            font-size: 16px;
            line-height: 24px;
        }
    }
`;

export default OrderModal;
